/**
 * Routines route contracts (design 5.3.1): one endpoint per
 * frontend-consumed (method, path) under /api/routines. The worker
 * registers its handlers through these (worker/src/features/routines/
 * api.ts); the frontend client (frontend/src/features/routines/client.js)
 * invokes the same objects — the path literal exists exactly once, here.
 * Same-path method pairs (GET/POST /api/routines, GET/PUT
 * /api/routines/default-prompt, GET/DELETE run detail) are separate
 * contracts by design.
 */

import { defineContracts, endpoint } from '../featureModule'
import { identity } from './routines'

export const contracts = defineContracts(identity, {
  /** GET /api/routines?scope=shared|personal — list routines with last-run info. */
  list: endpoint('GET', '/api/routines', { entitlement: 'routines' }),
  /** GET /api/routines/:id — full record for the editor on open. */
  detail: endpoint('GET', '/api/routines/:id', { entitlement: 'routines' }),
  /** POST /api/routines — create a routine. */
  create: endpoint('POST', '/api/routines', { entitlement: 'routines' }),
  /** PUT /api/routines/:id — update a routine (fields, schedule, enabled toggle). */
  update: endpoint('PUT', '/api/routines/:id', { entitlement: 'routines' }),
  /** DELETE /api/routines/:id — delete a routine (default routines refuse). */
  remove: endpoint('DELETE', '/api/routines/:id', { entitlement: 'routines' }),
  /** POST /api/routines/:id/run — manual trigger. */
  run: endpoint('POST', '/api/routines/:id/run', { entitlement: 'routines' }),
  /** GET /api/routines/:id/runs?limit&offset&scope — run history list. */
  listRuns: endpoint('GET', '/api/routines/:id/runs', { entitlement: 'routines' }),
  /** DELETE /api/routines/:id/runs — delete all runs for a routine. */
  deleteAllRuns: endpoint('DELETE', '/api/routines/:id/runs', { entitlement: 'routines' }),
  /** GET /api/routines/:id/runs/:runId?scope — single run report. */
  getRun: endpoint('GET', '/api/routines/:id/runs/:runId', { entitlement: 'routines' }),
  /** DELETE /api/routines/:id/runs/:runId — delete a single run. */
  deleteRun: endpoint('DELETE', '/api/routines/:id/runs/:runId', { entitlement: 'routines' }),
  /** GET /api/routines/default-prompt — custom baseline prompt (+ default). */
  getDefaultPrompt: endpoint('GET', '/api/routines/default-prompt', { entitlement: 'routines' }),
  /** PUT /api/routines/default-prompt — update the custom baseline prompt. */
  updateDefaultPrompt: endpoint('PUT', '/api/routines/default-prompt', { entitlement: 'routines' }),
  /** GET /api/routines/templates — template catalog + configured integrations. */
  templates: endpoint('GET', '/api/routines/templates', { entitlement: 'routines' }),
  /** POST /api/routines/generate-prompt — AI-assisted prompt generation (RoutineModal). */
  generatePrompt: endpoint('POST', '/api/routines/generate-prompt', { entitlement: 'routines' }),
  /** POST /api/routines/:id/transfer — owner assigns a new owner (routine disabled until they re-enable). */
  transfer: endpoint('POST', '/api/routines/:id/transfer', { entitlement: 'routines' }),
  /** POST /api/routines/:id/claim — claim an ORPHANED routine (disabled until the claimer enables). */
  claim: endpoint('POST', '/api/routines/:id/claim', { entitlement: 'routines' }),
  /** PUT /api/routines/:id/visibility — owner toggles private|shared (shared = view-only for others). */
  setVisibility: endpoint('PUT', '/api/routines/:id/visibility', { entitlement: 'routines' }),
  /**
   * PUT /api/routines/:id/mail-send { enabled } — the owner switches the
   * routine's standing mail approval ("May send mail": its ops.mail.send goes
   * out as the shared mailbox without a per-send approval, to "May write to"
   * recipients only). Turning it on needs the mail-approval right; every
   * switch is recorded. → { mailSend, changes }.
   */
  setMailSend: endpoint('PUT', '/api/routines/:id/mail-send', { entitlement: 'routines' }),
})
